import {
  infiniteQueryOptions,
  useInfiniteQuery,
  useMutation,
  useSuspenseInfiniteQuery,
} from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import type { ClientActionParams, ClientsPageResponse } from '@/pages/ClientsTab/types.ts';
import { HttpService } from '@/services/HttpService.ts';

const PAGE_SIZE = 20;

const getClients = async (accountantExternalId: string, page: number, search?: string) => {
  const { data } = await HttpService.get<ClientsPageResponse>(
    `/v1/accountant/${encodeURIComponent(accountantExternalId)}/clients`,
    { params: { page, size: PAGE_SIZE, ...(search && { search }) } }
  );
  return data;
};

export const clientsQueryKeyPrefix = (accountantExternalId: string) =>
  ['clients', accountantExternalId] as const;

export const clientsQueryKey = (accountantExternalId: string, search?: string) =>
  [...clientsQueryKeyPrefix(accountantExternalId), search ?? ''] as const;

export const clientsInfiniteQueryOptions = (accountantExternalId: string, search?: string) =>
  infiniteQueryOptions({
    queryKey: clientsQueryKey(accountantExternalId, search),
    queryFn: ({ pageParam }) => getClients(accountantExternalId, pageParam, search),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => (lastPage.last ? undefined : lastPage.number + 1),
  });

export const useClients = (accountantExternalId: string, search?: string) => {
  return useSuspenseInfiniteQuery(clientsInfiniteQueryOptions(accountantExternalId, search));
};

export const useHasAnyStatementAvailable = (accountantExternalId: string) => {
  const { data } = useInfiniteQuery(clientsInfiniteQueryOptions(accountantExternalId));
  return data?.pages.some((page) => page.content.some((c) => c.latestStatementAvailable)) ?? false;
};

export const getClient = async (
  queryClient: QueryClient,
  accountantExternalId: string,
  clientExternalId: string
) => {
  const data = await queryClient.ensureInfiniteQueryData(
    clientsInfiniteQueryOptions(accountantExternalId)
  );
  return data.pages.flatMap((page) => page.content).find((c) => c.externalId === clientExternalId);
};

const downloadClientStatement = async ({
  accountantExternalId,
  referralExternalId,
}: ClientActionParams) => {
  const { data } = await HttpService.get<Blob>(
    `/v1/accountant/${encodeURIComponent(accountantExternalId)}/clients/${encodeURIComponent(referralExternalId)}/statement`,
    { responseType: 'blob' }
  );
  return data;
};

export const useDownloadClientStatement = () => {
  return useMutation({
    mutationFn: async (params: ClientActionParams) => {
      const blob = await downloadClientStatement(params);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'statement.pdf';
      link.click();
      URL.revokeObjectURL(url);
    },
  });
};

const downloadAllStatements = async (accountantExternalId: string) => {
  const { data } = await HttpService.get<Blob>(
    `/v1/accountant/${encodeURIComponent(accountantExternalId)}/clients/statements`,
    { responseType: 'blob' }
  );
  return data;
};

export const useDownloadAllStatements = (accountantExternalId: string) => {
  return useMutation({
    mutationFn: async () => {
      const blob = await downloadAllStatements(accountantExternalId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'statements.zip';
      link.click();
      URL.revokeObjectURL(url);
    },
  });
};
